import React, { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useCreateSkill } from '../apis/skill-registry';
import { useCreatePleriApiKey } from '../apis/pleri-api-key';
import { usePleriStatus } from '../apis/pleri-status';
import { toast } from 'react-hot-toast';

import { useTranslation } from 'react-i18next';

const DEFAULT_API_URL = `https://${process.env.PUBLIC_API_URL}`;

export const LimitedFunctionalityBanner: React.FC = () => {
  const { t } = useTranslation('pleri/onboarding');
  const [isActivating, setIsActivating] = useState(false);
  const { mutateAsync: createSkill } = useCreateSkill();
  const { mutateAsync: createApiKey } = useCreatePleriApiKey();
  const { refetch: refetchPleriStatus, data: pleriStatus } = usePleriStatus();

  const handleActivatePlerionTool = async () => {
    if (isActivating) {
      return;
    }

    setIsActivating(true);
    try {
      // Generate API key
      const response = await createApiKey();
      const apiKey = response.apiKey;

      // Create/enable the Plerion skill
      await createSkill({
        skillType: 'plerion',
        enabled: true,
        config: {
          apiBaseUrl: DEFAULT_API_URL,
        },
        secrets: {
          apiBaseUrl: DEFAULT_API_URL,
          apiKey,
        },
      });

      // Show success toast
      toast.success(t('successfullyActivatedPlerionTool'));

      // Revalidate Pleri status
      await refetchPleriStatus();
    } catch (error) {
      console.error('Failed to activate Plerion tool:', error);
      toast.error(t('failedToActivatePlerionToolPleaseTry'));
    } finally {
      setIsActivating(false);
    }
  };

  if (!pleriStatus?.isLimitedFunctionality) {
    return null;
  }

  return (
    <div className="mb-4 w-full">
      <div className="flex items-center gap-2.5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm ring-1 ring-amber-100 dark:bg-amber-950">
        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-amber-600" />
        <span>
          {t('plerionToolIsNotEnabledWithoutThis')}{' '}
          <button
            onClick={handleActivatePlerionTool}
            disabled={isActivating}
            className="ml-2 rounded-md bg-[#FF732E] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#E65A1F] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isActivating ? t('activating') : t('activatePlerionTool')}
          </button>
        </span>
      </div>
    </div>
  );
};
